import { useCallback } from 'react';
import { ApiResult, useApiGet } from '@sierralabs/react-material-ui';

import {
  FetchApiCallback,
  useApiDelete,
  useApiPatch,
  useApiPost
} from '../../common/api/Api.hooks';
import { PaginatedList } from '../../common/PaginatedList.interface';
import { TaskType, TaskTypeListItem } from './TaskType.interface';

export function useGetTaskTypes(): [
  ApiResult<TaskType[]>,
  (filter?: Partial<TaskType>) => void
] {
  const { data, isLoading, error, get } = useApiGet<TaskType[]>('task-type');
  const getTaskTypes = useCallback(
    (filter?: Partial<TaskType>) => get(filter),
    [get]
  );
  return [{ data, isLoading, error }, getTaskTypes];
}

export function useFindTaskTypes(): [
  ApiResult<TaskType[]>,
  (query?: string, loadRelations?: boolean) => void
] {
  const { data, isLoading, error, get } =
    useApiGet<TaskType[]>('task-type/search');
  const findTaskTypes = useCallback(
    (query, loadRelations) =>
      get({ query: query || '', loadRelations: !!loadRelations }),
    [get]
  );
  return [{ data, isLoading, error }, findTaskTypes];
}

export function useFindTaskTypeList(): [
  ApiResult<PaginatedList<TaskTypeListItem>>,
  FetchApiCallback
] {
  const { get, ...result } =
    useApiGet<PaginatedList<TaskTypeListItem>>('task-type/list');
  return [result, get];
}

export function useFindTaskTypeEntities(): [
  ApiResult<string[]>,
  FetchApiCallback
] {
  const { get, ...result } = useApiGet<string[]>('task-type/entities');
  return [result, get];
}

export function useCreateTaskType(): [
  ApiResult<TaskType>,
  (dto: Partial<TaskType>) => void
] {
  const { post, ...result } = useApiPost<TaskType>('task-type');
  const createTaskType = useCallback(
    (dto: Partial<TaskType>) => {
      post(dto);
    },
    [post]
  );
  return [result, createTaskType];
}

export function useUpdateTaskType(): [
  ApiResult<TaskType>,
  (dto: Partial<TaskType>) => void
] {
  const { patch, ...result } = useApiPatch<TaskType>('task-type');
  const updateTaskType = useCallback(
    (dto: Partial<TaskType>) => {
      patch(dto);
    },
    [patch]
  );
  return [result, updateTaskType];
}

export function useDeleteTaskType(
  name: string
): [ApiResult<TaskType>, FetchApiCallback] {
  const { del, ...result } = useApiDelete<TaskType>(`task-type/${name}`);
  return [result, del];
}
